import { FormikProvider, useFormik } from 'formik';
import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';

import Alert, { AlertSeverity } from '@perpay-web/components/base/Alert/Alert';
import BackButton from '@perpay-web/components/base/BackButton/BackButton';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import NonFieldErrors from '@perpay-web/components/base/NonFieldErrors/NonFieldErrors';
import FormikPasswordInput from '@perpay-web/components/base/PasswordInput/FormikPasswordInput';
import FormikTextInput from '@perpay-web/components/base/TextInput/FormikTextInput';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusHeader from '@perpay-web/components/composite/FocusLayout/FocusHeader';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import Captcha from '@perpay-web/fintech/components/base/Captcha/Captcha';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import MarketplaceApplicationHeader from '@perpay-web/fintech/components/base/Header/MarketplaceApplicationHeader';
import { PAYCHEX } from '@perpay-web/fintech/constants/partnerOnboardingCodes';
import { useFullHeader } from '@perpay-web/fintech/hooks/useFullHeaderContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import type {
    LoginFormValues,
    MFAData,
} from '@perpay-web/fintech/types/authentication';
import { loginFormSchema } from '@perpay-web/fintech/validators/formBased';
import { useIsMobile } from '@perpay-web/hooks/useIsMobile';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';
import { validateForm } from '@perpay-web/validators/formBased';

import PaychexLoginBanner from './PaychexLoginBanner';
import styles from './PaychexLoginForm.scss';

type FormErrors = {
    [key: string]: string[];
};

type PaychexLoginFormProps = {
    onSubmit: (values: LoginFormValues) => void;
    errors?: FormErrors;
    isLoading?: boolean;
    mfa?: MFAData;
    initialValues?: LoginFormValues;
    showBanner?: boolean;
    showWafCaptcha?: boolean;
};

const PaychexLoginForm = ({
    onSubmit,
    errors = {},
    isLoading = false,
    mfa = {},
    initialValues = {
        email_or_phone: '',
        password: '',
    },
    showBanner = false,
    showWafCaptcha = false,
}: PaychexLoginFormProps): ReactElement => {
    const { isMobile } = useIsMobile();

    const renderHeaderCallback = useCallback(
        (): ReactElement | null =>
            isMobile ? null : (
                <MarketplaceApplicationHeader partnerCode={PAYCHEX} />
            ),
        [isMobile],
    );
    useFullHeader(renderHeaderCallback);

    const formik = useFormik<LoginFormValues>({
        initialValues,
        onSubmit: (values: LoginFormValues): void => {
            const isEmail = values.email_or_phone.includes('@');

            const emailOrPhone =
                isEmail || values.email_or_phone.startsWith('+1')
                    ? values.email_or_phone
                    : `+1${values.email_or_phone}`;

            const newValues: LoginFormValues = {
                ...values,
                email_or_phone: emailOrPhone,
            };
            onSubmit(newValues);
        },
        validate: (values: LoginFormValues) =>
            validateForm(values, loginFormSchema),
        validateOnChange: false,
    });

    const mfaAlert = useMemo(
        (): ReactElement | null =>
            mfa.error?.throttle ? (
                <Alert
                    content={mfa.error.throttle.message[0]}
                    severity={AlertSeverity.DANGER}
                    className={styles['throttle-alert']}
                />
            ) : null,
        [mfa],
    );

    const onCaptchaSuccessCallback = (): void => {
        formik.submitForm();
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <EndToEndTestIdProvider testId='paychex-login-form'>
                    {!isMobile && mfaAlert}
                    <FormikProvider value={formik}>
                        <form onSubmit={formik.handleSubmit}>
                            <FocusLayout className={styles['focus-layout']}>
                                <FocusHeader
                                    className={styles['focus-header']}
                                    leftButtonElement={
                                        <BackButton
                                            href={paths.paychexSignup.path}
                                        />
                                    }
                                />
                                {showBanner ? <PaychexLoginBanner /> : null}
                                <FocusContent
                                    className={styles['focus-content']}
                                >
                                    {isMobile ? mfaAlert : null}
                                    <Stack className={styles['content-stack']}>
                                        <div className={styles.title}>
                                            Login to Perpay
                                        </div>
                                        <Stack
                                            className={styles['inner-stack']}
                                        >
                                            <EndToEndTestIdProvider testId='paychex-login-email-input'>
                                                <FormikTextInput
                                                    label='Email address or phone number'
                                                    placeholder='me@example.com or 5551234567'
                                                    name='email_or_phone'
                                                    type='text'
                                                    inputMode='email'
                                                    errors={errors}
                                                />
                                            </EndToEndTestIdProvider>
                                            <EndToEndTestIdProvider testId='paychex-login-password-input'>
                                                <FormikPasswordInput
                                                    name='password'
                                                    errors={errors}
                                                />
                                            </EndToEndTestIdProvider>
                                            <CustomLink
                                                href={paths.resetPassword.path}
                                            >
                                                Forgot password?
                                            </CustomLink>
                                            <NonFieldErrors errors={errors} />
                                            <Captcha
                                                showWafCaptcha={showWafCaptcha}
                                                onSuccessCallback={
                                                    onCaptchaSuccessCallback
                                                }
                                            />
                                        </Stack>
                                    </Stack>
                                </FocusContent>
                                <FocusFooter>
                                    <EndToEndTestIdProvider testId='paychex-login-button'>
                                        <CustomButton
                                            action='primary'
                                            loading={isLoading}
                                            type='submit'
                                        >
                                            Login
                                        </CustomButton>
                                    </EndToEndTestIdProvider>
                                </FocusFooter>
                            </FocusLayout>
                        </form>
                    </FormikProvider>
                </EndToEndTestIdProvider>
            </div>
        </div>
    );
};

export default PaychexLoginForm;
